import React from "react";
import { Check } from "lucide-react";
import { Section, Kicker, Heading, Button, Rule } from "./ui";
import Reveal from "./Reveal";

// Data-driven pillar page shared by Agency Acquisitions & Property Portfolios
const PillarPage = ({ content }) => {
  const {
    kicker,
    title,
    subtitle,
    criteriaKicker,
    criteriaTitle,
    criteriaIntro,
    criteria,
    whyKicker,
    whyTitle,
    whyBlocks,
    processKicker,
    processTitle,
    steps,
    proofKicker,
    proofTitle,
    proofIntro,
    proofItems,
    ctaTitle,
    ctaText,
    ctaButtonLabel,
    ctaTo,
  } = content;

  return (
    <>
      {/* Hero */}
      <section className="bg-navy text-white pt-36 pb-24 lg:pt-44 lg:pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Kicker light>{kicker}</Kicker>
            <Heading as="h1" className="text-4xl md:text-5xl lg:text-6xl max-w-4xl">
              {title}
            </Heading>
            <p className="mt-8 text-lg text-steel-light max-w-2xl leading-relaxed">{subtitle}</p>
          </Reveal>
        </div>
      </section>

      {/* Criteria */}
      <Section stone>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Kicker>{criteriaKicker}</Kicker>
              <Heading className="text-3xl md:text-4xl">{criteriaTitle}</Heading>
              <Rule className="mt-6" />
              <p className="mt-6 text-steel leading-relaxed max-w-md">{criteriaIntro}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-5">
              {criteria.map((c, i) => (
                <Reveal as="li" key={i} delay={i * 0.05}>
                  <div className="flex items-start gap-4 border-b border-navy/10 pb-5">
                    <Check size={20} className="text-steel mt-0.5 shrink-0" />
                    <span className="text-navy/90 leading-relaxed">{c}</span>
                  </div>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Why sell to us */}
      <Section>
        <Reveal>
          <div className="max-w-2xl">
            <Kicker>{whyKicker}</Kicker>
            <Heading className="text-3xl md:text-4xl">{whyTitle}</Heading>
          </div>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-px bg-navy/10">
          {whyBlocks.map((b, i) => {
            const Icon = b.icon;
            return (
              <Reveal key={i} delay={i * 0.06}>
                <div className="bg-white p-10 h-full">
                  <Icon size={28} className="text-steel" strokeWidth={1.5} />
                  <h3 className="mt-6 text-xl font-semibold tracking-heading">{b.title}</h3>
                  <p className="mt-3 text-steel leading-relaxed">{b.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* Process */}
      <Section stone>
        <Reveal>
          <div className="max-w-2xl">
            <Kicker>{processKicker}</Kicker>
            <Heading className="text-3xl md:text-4xl">{processTitle}</Heading>
          </div>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="relative">
                <span className="text-5xl font-light text-steel-light/70 tracking-heading">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="mt-4 h-px w-full bg-navy/10" />
                <h3 className="mt-5 text-lg font-semibold tracking-heading">{s.title}</h3>
                <p className="mt-2 text-sm text-steel leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Proof / track record */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <Kicker>{proofKicker}</Kicker>
              <Heading className="text-3xl md:text-4xl">{proofTitle}</Heading>
              <Rule className="mt-6" />
              <p className="mt-6 text-steel leading-relaxed max-w-md">{proofIntro}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {proofItems.map((p, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="border border-navy/10 bg-white h-full flex flex-col overflow-hidden">
                    {p.logo ? (
                      <>
                        <div className="aspect-[16/9] bg-white border-b border-navy/10 flex items-center justify-center p-6">
                          <img
                            src={p.logo}
                            alt={`${p.name} logo`}
                            loading="lazy"
                            className="max-h-12 md:max-h-14 max-w-[80%] w-auto object-contain"
                          />
                        </div>
                        <div className="p-8">
                          <h3 className="text-lg font-semibold tracking-heading mb-2">{p.name}</h3>
                          <p className="text-sm text-steel leading-relaxed">{p.text}</p>
                        </div>
                      </>
                    ) : (
                      <div className="p-8">
                        <div className="h-10 flex items-center">
                          <span className="text-xl font-semibold tracking-heading">{p.name}</span>
                        </div>
                        <p className="mt-3 text-sm text-steel leading-relaxed">{p.text}</p>
                      </div>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* CTA banner */}
      <section className="bg-navy text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-24">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <Heading className="text-3xl md:text-4xl">{ctaTitle}</Heading>
              <p className="mt-4 text-steel-light leading-relaxed">{ctaText}</p>
            </div>
            <Button to={ctaTo} variant="primaryLight" testid="pillar-cta-button" className="shrink-0">
              {ctaButtonLabel}
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default PillarPage;
